<template>
  <div class="po-wrap">
    <div class="po-header">
      <div class="po-brand">
        <i class="fas fa-paper-plane"></i>
        <span>STARSENDER</span>
      </div>
      <a href="https://app.starsender.online/login" class="po-login-link">Masuk Dashboard</a>
    </div>

    <div v-if="referralValid" class="po-referral-banner" role="status">
      <div class="po-referral-banner-icon"><i class="fas fa-user-friends"></i></div>
      <div class="po-referral-banner-body">
        <div class="po-referral-banner-title">Pesanan melalui referral</div>
        <div class="po-referral-banner-line">
          <strong>{{ referralDisplayName }}</strong>
          <span class="po-referral-banner-code">(kode {{ referralCode }})</span>
        </div>
        <div v-if="referralEmailMasked" class="po-referral-banner-sub">{{ referralEmailMasked }}</div>
      </div>
    </div>

    <!-- SUCCESS / DONE STATE -->
    <div v-if="done" class="po-card po-done">
      <div class="po-done-icon"><i class="fas fa-check"></i></div>
      <h3 v-if="doneManual && isRenewal">Konfirmasi perpanjangan berhasil dikirim!</h3>
      <h3 v-else-if="doneManual">Konfirmasi transfer berhasil dikirim!</h3>
      <h3 v-else-if="isRenewal">Terima kasih! Perpanjangan paket sedang diproses</h3>
      <h3 v-else>Terima kasih! Pesanan Anda sedang diproses</h3>
      <p v-if="doneManual && isRenewal">
        Tim kami akan verifikasi bukti transfer (maks. <b>1×24 jam</b>).
        Setelah disetujui, masa aktif paket Anda otomatis diperpanjang. Silakan masuk dengan akun Anda.
      </p>
      <p v-else-if="doneManual">
        Akun Anda sudah dibuat. Tim kami akan verifikasi bukti transfer (maks. <b>1×24 jam</b>).
        Setelah disetujui, paket otomatis aktif. Silakan masuk dengan email yang Anda daftarkan.
      </p>
      <p v-else-if="isRenewal">
        Begitu pembayaran Anda terkonfirmasi, <b>masa aktif paket akan otomatis diperpanjang</b>.
        Silakan masuk menggunakan akun StarSender Anda.
      </p>
      <p v-else>
        Begitu pembayaran Anda terkonfirmasi, <b>paket akan otomatis aktif</b> di akun Anda.
        Silakan masuk menggunakan email yang Anda daftarkan.
      </p>
      <a href="https://app.starsender.online/login" class="po-btn po-btn-primary" style="max-width:280px;margin:8px auto 0">
        Masuk ke Dashboard
      </a>
    </div>

    <!-- ORDER FORM -->
    <div v-else>
      <div class="po-title">
        <h2>Pesan Paket StarSender</h2>
        <p>Pilih sesuai kondisi Anda — tidak perlu daftar ulang jika sudah punya akun.</p>
      </div>

      <div class="po-mode-tabs">
        <button type="button" class="po-mode-tab" :class="{ active: flowChoice === 'new' }" @click="setFlowChoice('new')">
          <i class="fas fa-user-plus"></i>
          <div class="po-mode-tab-text">
            <strong>Belum Punya Akun</strong>
            <span>Daftar &amp; pesan paket baru</span>
          </div>
        </button>
        <button type="button" class="po-mode-tab" :class="{ active: flowChoice === 'renew' }" @click="setFlowChoice('renew')">
          <i class="fas fa-sync-alt"></i>
          <div class="po-mode-tab-text">
            <strong>Sudah Punya Akun</strong>
            <span>Perpanjang paket — tanpa daftar ulang</span>
          </div>
        </button>
      </div>

      <div v-if="flowChoice === 'renew'" class="po-mode-hint po-mode-hint-renew">
        <i class="fas fa-info-circle"></i>
        <template v-if="sessionPrefilled">
          Data akun sudah kami isi dari sesi login Anda. Kalau perlu ganti, edit email atau WhatsApp lalu tunggu pengecekan otomatis.
        </template>
        <template v-else>
          Masukkan email atau WhatsApp akun StarSender Anda. <b>Tidak perlu password</b> dan tidak perlu mendaftar lagi.
        </template>
      </div>
      <div v-else class="po-mode-hint">
        <i class="fas fa-info-circle"></i>
        Isi data di bawah untuk membuat akun StarSender baru. Sudah punya akun?
        <a href="#" @click.prevent="setFlowChoice('renew')"><b>Perpanjang paket di sini</b></a>
      </div>

      <div v-if="orderMode === 'renew' && currentPackage" class="po-renew-banner">
        <div><b>Akun ditemukan:</b> {{ form.email || form.phone }}</div>
        <div>Paket saat ini: <b>{{ currentPackage.name }}</b>
          <span :class="currentPackage.is_expired ? 'po-expired' : 'po-active'">
            — {{ currentPackage.is_expired ? 'Kedaluwarsa' : 'Aktif' }}
            <template v-if="currentPackage.days_left >= 0"> ({{ currentPackage.days_left }} hari tersisa)</template>
          </span>
        </div>
      </div>

      <div v-if="flowChoice === 'new' && existingHint" class="po-warn" style="margin-bottom:14px">
        Email/telepon ini sudah terdaftar.
        <a href="#" @click.prevent="setFlowChoice('renew')"><b>Gunakan Perpanjang Paket</b></a>
        — tidak perlu mendaftar lagi.
      </div>

      <div v-if="flowChoice === 'renew' && accountNotFound" class="po-error" style="margin-bottom:14px">
        Akun tidak ditemukan. Periksa email/WhatsApp Anda, atau
        <a href="#" @click.prevent="setFlowChoice('new')"><b>daftar sebagai pengguna baru</b></a>.
      </div>

      <div v-if="orderMode === 'existing' && flowChoice === 'new'" class="po-error" style="margin-bottom:14px">
        Akun sudah terdaftar. Silakan
        <a href="#" @click.prevent="setFlowChoice('renew')"><b>perpanjang paket di sini</b></a>
        atau <a href="https://app.starsender.online/login"><b>masuk dashboard</b></a>.
      </div>

      <div v-if="loading" class="po-loading"><i class="fas fa-spinner fa-spin"></i> Memuat...</div>

      <div v-else class="po-grid">
        <!-- LEFT: form -->
        <div class="po-col-main">
          <!-- 1. Pilih Paket -->
          <div class="po-card">
            <div class="po-step"><span>1</span> {{ flowChoice === 'renew' ? 'Pilih Durasi Perpanjangan' : 'Pilih Paket' }}</div>

            <template v-if="flowChoice === 'renew'">
              <div class="po-group-label">Paket Bulanan</div>
              <div class="po-pkg-list">
                <label v-for="p in monthly" :key="p.id" class="po-pkg"
                       :class="{ active: packageIdMatch(selectedPackageId, p.id) }">
                  <input type="radio" :value="p.id" v-model.number="selectedPackageId" name="pkg">
                  <div class="po-pkg-body">
                    <div class="po-pkg-name">{{ p.name }}</div>
                    <div class="po-pkg-specs">{{ specs(p) }}</div>
                  </div>
                  <div class="po-pkg-price">{{ formatRp(p.price) }}</div>
                </label>
              </div>
            </template>

            <div class="po-group-label" :class="{ 'mt-3': flowChoice === 'renew' }">Paket 6 Bulan</div>
            <div class="po-pkg-list">
              <label v-for="p in sixMonth" :key="p.id" class="po-pkg"
                     :class="{ active: packageIdMatch(selectedPackageId, p.id) }">
                <input type="radio" :value="p.id" v-model.number="selectedPackageId" name="pkg">
                <div class="po-pkg-body">
                  <div class="po-pkg-name">{{ p.name }}</div>
                  <div class="po-pkg-specs">{{ specs(p) }}</div>
                </div>
                <div class="po-pkg-price">{{ formatRp(p.price) }}</div>
              </label>
            </div>

            <div class="po-group-label mt-3">Paket 1 Tahun</div>
            <div class="po-pkg-list">
              <label v-for="p in yearly" :key="p.id" class="po-pkg"
                     :class="{ active: packageIdMatch(selectedPackageId, p.id) }">
                <input type="radio" :value="p.id" v-model.number="selectedPackageId" name="pkg">
                <div class="po-pkg-body">
                  <div class="po-pkg-name">{{ p.name }}</div>
                  <div class="po-pkg-specs">{{ specs(p) }}</div>
                </div>
                <div class="po-pkg-price">{{ formatRp(p.price) }}</div>
              </label>
            </div>

            <div v-if="lifetime.length" class="po-group-label mt-3">Paket Lifetime</div>
            <div v-if="lifetime.length" class="po-pkg-list">
              <label v-for="p in lifetime" :key="p.id" class="po-pkg"
                     :class="{ active: packageIdMatch(selectedPackageId, p.id) }">
                <input type="radio" :value="p.id" v-model.number="selectedPackageId" name="pkg">
                <div class="po-pkg-body">
                  <div class="po-pkg-name">{{ displayName(p) }}</div>
                  <div class="po-pkg-specs">{{ specs(p) }}</div>
                </div>
                <div class="po-pkg-price">{{ formatRp(p.price) }}</div>
              </label>
            </div>
          </div>

          <!-- 2. Data Akun -->
          <div class="po-card">
            <div class="po-step"><span>2</span> {{ flowChoice === 'renew' ? 'Identifikasi Akun' : 'Data Akun Baru' }}</div>
            <div v-if="lookupLoading" class="po-lookup"><i class="fas fa-spinner fa-spin"></i> Mengecek akun...</div>
            <template v-if="flowChoice === 'renew'">
              <p class="po-field-note">Masukkan email atau nomor WhatsApp yang terdaftar di StarSender.</p>
              <div class="po-field">
                <label>Email</label>
                <input type="email" v-model.trim="form.email" placeholder="email@anda.com" @blur="scheduleLookup">
              </div>
              <div class="po-field">
                <label>No. WhatsApp</label>
                <input type="text" v-model.trim="form.phone" placeholder="08xxxxxxxxxx" @blur="scheduleLookup">
              </div>
              <div v-if="orderMode === 'renew' && form.name" class="po-field">
                <label>Nama</label>
                <input type="text" :value="form.name" disabled>
              </div>
            </template>
            <template v-else>
              <div class="po-field">
                <label>Nama Lengkap</label>
                <input type="text" v-model.trim="form.name" placeholder="Nama Anda">
              </div>
              <div class="po-field">
                <label>Email</label>
                <input type="email" v-model.trim="form.email" placeholder="email@anda.com" @blur="scheduleLookup">
              </div>
              <div class="po-field">
                <label>No. WhatsApp</label>
                <input type="text" v-model.trim="form.phone" placeholder="08xxxxxxxxxx" @blur="scheduleLookup">
              </div>
              <div class="po-field">
                <label>Password</label>
                <input type="password" v-model="form.password" placeholder="Min. 6 karakter">
                <small>Digunakan untuk masuk ke dashboard setelah pembayaran.</small>
              </div>
            </template>
          </div>

          <!-- 3. Metode Pembayaran -->
          <div class="po-card">
            <div class="po-step"><span>3</span> Metode Pembayaran</div>

            <div v-if="!hasAnyPaymentMethod" class="po-error">
              Tidak ada metode pembayaran tersedia saat ini. Silakan hubungi admin.
            </div>

            <div v-else class="po-pay-tabs">
              <button v-if="paymentMethods.tripay_enabled" type="button" class="po-pay-tab"
                :class="{ active: payType === 'tripay' }" @click="payType = 'tripay'">
                <i class="fas fa-credit-card"></i> Tripay (VA / QRIS)
              </button>
              <button v-if="paymentMethods.starqris_enabled" type="button" class="po-pay-tab"
                :class="{ active: payType === 'starqris' }" @click="payType = 'starqris'">
                <i class="fas fa-qrcode"></i> StarQRIS
              </button>
              <button v-if="paymentMethods.manual_transfer_enabled" type="button" class="po-pay-tab"
                :class="{ active: payType === 'manual' }" @click="payType = 'manual'">
                <i class="fas fa-university"></i> Transfer Manual
              </button>
            </div>

            <div v-if="payType === 'starqris' && paymentMethods.starqris_enabled" class="po-starqris-hint">
              <i class="fas fa-qrcode"></i>
              Scan QRIS dinamis — paket aktif otomatis setelah pembayaran berhasil.
              <div v-if="finalTotal > 10000000" class="po-error" style="margin-top:8px">
                Nominal melebihi batas StarQRIS (max Rp 10.000.000). Pilih Tripay atau transfer manual.
              </div>
            </div>

            <!-- Tripay channels -->
            <div v-if="payType === 'tripay' && paymentMethods.tripay_enabled">
              <div v-if="channelGroups.length === 0" class="po-error" style="margin-top:10px">
                Metode Tripay belum tersedia.
              </div>
              <div v-for="grp in channelGroups" :key="grp.name" class="po-chgroup">
                <div class="po-group-label">{{ grp.name }}</div>
                <div class="po-ch-list">
                  <label v-for="ch in grp.items" :key="ch.channel_code" class="po-ch"
                         :class="{ active: selectedChannel === ch.channel_code }">
                    <input type="radio" :value="ch.channel_code" v-model="selectedChannel" name="ch">
                    <img v-if="imgMap[ch.channel_code]" :src="imgMap[ch.channel_code]" :alt="ch.channel_name">
                    <span v-else class="po-ch-name">{{ ch.channel_name }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Manual transfer -->
            <div v-if="payType === 'manual' && paymentMethods.manual_transfer_enabled" class="po-manual">
              <div v-if="paymentMethods.instruction_text" class="po-manual-info">
                <i class="fas fa-info-circle"></i> {{ paymentMethods.instruction_text }}
              </div>
              <div class="po-group-label">Pilih Bank Tujuan</div>
              <div class="po-bank-list">
                <label v-for="b in paymentMethods.banks" :key="b.id" class="po-bank"
                       :class="{ active: manualForm.bank_account_id === b.id }">
                  <input type="radio" :value="b.id" v-model="manualForm.bank_account_id" name="bank">
                  <div>
                    <strong>{{ b.bank_name }}</strong>
                    <div><code>{{ b.account_number }}</code> a/n {{ b.account_holder }}</div>
                  </div>
                </label>
                <div v-if="!paymentMethods.banks || paymentMethods.banks.length === 0" class="po-error">
                  Belum ada rekening tersedia. Hubungi admin.
                </div>
              </div>
              <div class="po-group-label mt-3">Data Pengirim & Bukti</div>
              <div class="po-field">
                <label>Nama Pengirim *</label>
                <input type="text" v-model.trim="manualForm.sender_name" placeholder="Nama di rekening pengirim">
              </div>
              <div class="po-manual-row">
                <div class="po-field">
                  <label>Bank Pengirim</label>
                  <input type="text" v-model.trim="manualForm.sender_bank" placeholder="BCA / Mandiri">
                </div>
                <div class="po-field">
                  <label>No. Rek Pengirim</label>
                  <input type="text" v-model.trim="manualForm.sender_account" placeholder="Opsional">
                </div>
              </div>
              <div class="po-manual-row">
                <div class="po-field">
                  <label>Tanggal Transfer *</label>
                  <input type="datetime-local" v-model="manualForm.transfer_date_str">
                </div>
                <div class="po-field">
                  <label>Bukti Transfer *</label>
                  <input type="file" accept="image/*" @change="onProofChange">
                </div>
              </div>
              <div class="po-field">
                <label>Catatan (opsional)</label>
                <input type="text" v-model.trim="manualForm.user_note" placeholder="Catatan tambahan">
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: summary -->
        <div class="po-col-side">
          <div class="po-card po-summary">
            <div class="po-step"><span>4</span> Ringkasan & Bayar</div>

            <div class="po-sum-row">
              <span>Paket</span>
              <b>{{ selectedPackage ? selectedPackage.name : '-' }}</b>
            </div>
            <div class="po-sum-row">
              <span>Metode</span>
              <b>{{ payTypeLabel }}</b>
            </div>
            <div v-if="coupon.discount > 0" class="po-sum-row">
              <span>Diskon</span>
              <b class="po-discount">- {{ formatRp(coupon.discount) }}</b>
            </div>
            <div class="po-coupon">
              <label for="coupon-code">Kode Kupon</label>
              <div class="po-coupon-row">
                <input
                  id="coupon-code"
                  type="text"
                  v-model.trim="coupon.code"
                  placeholder="Masukkan kode kupon"
                  @keyup.enter="checkCouponCode"
                />
                <button type="button" class="po-coupon-btn" :disabled="coupon.loading || !coupon.code" @click="checkCouponCode">
                  {{ coupon.loading ? '...' : 'Apply' }}
                </button>
              </div>
              <small v-if="coupon.status === 'valid'" class="po-coupon-ok">{{ coupon.message }}</small>
              <small v-else-if="coupon.status === 'invalid'" class="po-coupon-bad">{{ coupon.message }}</small>
            </div>
            <div class="po-sum-total">
              <span>Total</span>
              <b>{{ formatRp(finalTotal) }}</b>
            </div>

            <!-- Kebijakan & Refund -->
            <div class="po-policy">
              <div class="po-policy-title"><i class="fas fa-file-contract"></i> Kebijakan Pembelian &amp; Refund</div>
              <ul>
                <li>Paket yang sudah dibeli <b>tidak dapat di-refund</b>.</li>
                <li>Termasuk bila nomor WhatsApp <b>diblokir/dibatasi</b> atau terkena <b>error 463</b> (kebijakan WhatsApp di luar kendali StarSender).</li>
                <li>Untuk kirim ke nomor yang belum pernah chat, tersedia solusi resmi via KlikPesan.id.</li>
              </ul>
              <label class="po-agree">
                <input type="checkbox" v-model="agreed">
                <span>Saya telah membaca &amp; menyetujui kebijakan di atas, dan memahami bahwa pembelian ini <b>non-refundable</b>.</span>
              </label>
            </div>

            <div v-if="errorMsg" class="po-error">
              {{ errorMsg }}
              <a v-if="existsLogin" href="https://app.starsender.online/login"><b>Login di sini</b></a>
            </div>

            <button class="po-btn po-btn-primary" :disabled="!canSubmit || submitting" @click="submit">
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <span v-else-if="flowChoice === 'renew'">{{ payType === 'manual' ? 'Kirim Konfirmasi Perpanjangan' : 'Perpanjang Sekarang' }}</span>
              <span v-else>{{ payType === 'manual' ? 'Kirim Konfirmasi Transfer' : 'Bayar Sekarang' }}</span>
            </button>
            <div class="po-secure">
              <i class="fas fa-lock"></i>
              <span v-if="payType === 'manual'">Transfer manual — verifikasi admin max 1×24 jam</span>
              <span v-else-if="payType === 'starqris'">Pembayaran aman via StarQRIS (QRIS)</span>
              <span v-else-if="paymentMethods.tripay_enabled">Pembayaran aman via Tripay</span>
              <span v-else>Pembayaran</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showStarQrisModal" class="po-modal-backdrop" @click.self="closeStarQrisModal">
        <div class="po-modal">
          <div class="po-modal-head">
            <i class="fas fa-qrcode"></i> Bayar via StarQRIS
          </div>
          <StarQrisPayment
            :transactionId="starqrisTransactionId"
            :data="starqrisPayment"
            :statusUrl="starqrisStatusUrl"
            successTitle="Pembayaran Berhasil!"
            :successMessage="starqrisSuccessMessage"
            @paid="onStarQrisPaid"
            @done="onStarQrisDone"
            @cancel="closeStarQrisModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import StarQrisPayment from "@/components/forms/billing/StarQrisPayment.vue";

export default {
  name: "OrderPublic",
  components: { StarQrisPayment },
  data() {
    return {
      loading: true,
      submitting: false,
      done: false,
      doneManual: false,
      isRenewal: false,
      flowChoice: "new",
      orderMode: "new",
      lookupLoading: false,
      currentPackage: null,
      existingHint: false,
      accountNotFound: false,
      lookupTimer: null,
      packages: [],
      channels: [],
      paymentMethods: {
        tripay_enabled: true,
        manual_transfer_enabled: false,
        starqris_enabled: false,
        banks: [],
        instruction_text: "",
      },
      showStarQrisModal: false,
      starqrisPayment: {},
      starqrisTransactionId: "",
      starqrisStatusUrl: "",
      payType: "tripay",
      selectedPackageId: null,
      selectedChannel: null,
      agreed: false,
      errorMsg: "",
      existsLogin: false,
      sessionPrefilled: false,
      form: { name: "", email: "", phone: "", password: "" },
      manualForm: {
        bank_account_id: null,
        sender_name: "",
        sender_bank: "",
        sender_account: "",
        transfer_date_str: "",
        user_note: "",
      },
      proofFile: null,
      coupon: { code: "", discount: 0, status: "", message: "", loading: false },
      referralCode: "",
      referralPartnerName: "",
      referralEmailMasked: "",
      referralValid: false,
      referralStorageKey: "starsender_order_ref",
      imgMap: {
        PERMATAVA: "https://assets.tripay.co.id/upload/payment-icon/szezRhAALB1583408731.png",
        BNIVA: "https://assets.tripay.co.id/upload/payment-icon/n22Qsh8jMa1583433577.png",
        BRIVA: "https://assets.tripay.co.id/upload/payment-icon/8WQ3APST5s1579461828.png",
        MANDIRIVA: "https://assets.tripay.co.id/upload/payment-icon/T9Z012UE331583531536.png",
        BCAVA: "https://assets.tripay.co.id/upload/payment-icon/ytBKvaleGy1605201833.png",
        MUAMALATVA: "https://assets.tripay.co.id/upload/payment-icon/GGwwcgdYaG1611929720.png",
        CIMBVA: "https://assets.tripay.co.id/upload/payment-icon/WtEJwfuphn1614003973.png",
        BSIVA: "https://assets.tripay.co.id/upload/payment-icon/tEclz5Assb1643375216.png",
        DANAMONVA: "https://assets.tripay.co.id/upload/payment-icon/F3pGzDOLUz1644245546.png",
        ALFAMART: "https://assets.tripay.co.id/upload/payment-icon/jiGZMKp2RD1583433506.png",
        INDOMARET: "https://assets.tripay.co.id/upload/payment-icon/zNzuO5AuLw1583513974.png",
        ALFAMIDI: "https://assets.tripay.co.id/upload/payment-icon/aQTdaUC2GO1593660384.png",
        QRIS: "https://assets.tripay.co.id/upload/payment-icon/BpE4BPVyIw1605597490.png",
        QRIS2: "https://assets.tripay.co.id/upload/payment-icon/8ewGzP6SWe1649667701.png",
        QRISC: "https://assets.tripay.co.id/upload/payment-icon/BpE4BPVyIw1605597490.png",
        OVO: "https://assets.tripay.co.id/upload/payment-icon/JqUFM2ETy01622344163.png",
        DANA: "https://assets.tripay.co.id/upload/payment-icon/cJzQrMYDqI1644243832.png",
        SHOPEEPAY: "https://assets.tripay.co.id/upload/payment-icon/T9Z012UE331622522657.png",
      },
    };
  },
  computed: {
    monthly() {
      return this.packages.filter((p) => (p.duration || 0) >= 25 && (p.duration || 0) <= 35);
    },
    sixMonth() {
      return this.packages.filter((p) => (p.duration || 0) > 35 && (p.duration || 0) <= 183);
    },
    yearly() {
      const d = (p) => p.duration || 0;
      return this.packages.filter((p) => d(p) >= 365 && d(p) < 1000);
    },
    lifetime() {
      return this.packages.filter((p) => (p.duration || 0) >= 1000);
    },
    activeChannels() {
      return this.channels.filter((c) => c.is_active === 1);
    },
    channelGroups() {
      const order = ["Virtual Account", "E-Wallet", "Convenience Store", "QRIS"];
      const map = {};
      this.activeChannels.forEach((c) => {
        const g = c.group || "Lainnya";
        if (!map[g]) map[g] = [];
        map[g].push(c);
      });
      const groups = Object.keys(map).map((name) => ({ name, items: map[name] }));
      groups.sort((a, b) => {
        const ia = order.indexOf(a.name);
        const ib = order.indexOf(b.name);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
      return groups;
    },
    selectedPackage() {
      return this.findPackageById(this.selectedPackageId);
    },
    selectedChannelName() {
      const c = this.activeChannels.find((x) => x.channel_code === this.selectedChannel);
      return c ? c.channel_name : "";
    },
    payTypeLabel() {
      if (this.payType === "manual") return "Transfer Manual";
      if (this.payType === "starqris") return "StarQRIS (QRIS)";
      return this.selectedChannelName || "Tripay";
    },
    hasAnyPaymentMethod() {
      return (
        this.paymentMethods.tripay_enabled ||
        this.paymentMethods.manual_transfer_enabled ||
        this.paymentMethods.starqris_enabled
      );
    },
    starqrisSuccessMessage() {
      if (this.flowChoice === "renew") {
        return "Masa aktif paket Anda akan segera diperpanjang. Silakan masuk ke dashboard.";
      }
      return "Paket Anda aktif. Silakan masuk ke dashboard dengan email yang Anda daftarkan.";
    },
    canSubmit() {
      if (this.flowChoice === "renew" && this.orderMode !== "renew") return false;
      if (this.flowChoice === "new" && this.orderMode === "existing") return false;
      const baseRenew =
        this.selectedPackageId &&
        (this.form.email || this.form.phone) &&
        this.orderMode === "renew" &&
        this.agreed &&
        this.hasAnyPaymentMethod;
      const baseNew =
        this.selectedPackageId &&
        this.form.name &&
        this.form.email &&
        this.form.phone &&
        this.form.password.length >= 6 &&
        this.agreed &&
        this.hasAnyPaymentMethod &&
        this.orderMode !== "existing";
      const base = this.flowChoice === "renew" ? baseRenew : baseNew;
      if (!base) return false;
      if (this.payType === "manual") {
        return (
          this.manualForm.bank_account_id &&
          this.manualForm.sender_name &&
          this.manualForm.transfer_date_str &&
          this.proofFile
        );
      }
      if (this.payType === "starqris") {
        return (
          this.paymentMethods.starqris_enabled &&
          this.finalTotal >= 10000 &&
          this.finalTotal <= 10000000
        );
      }
      return this.paymentMethods.tripay_enabled && this.selectedChannel;
    },
    finalTotal() {
      const price = this.selectedPackage ? Number(this.selectedPackage.price) || 0 : 0;
      const disc = Number(this.coupon.discount) || 0;
      return Math.max(0, price - disc);
    },
    referralDisplayName() {
      return (this.referralPartnerName || this.referralCode || "").trim();
    },
  },
  watch: {
    selectedPackageId() {
      this.resetCoupon();
    },
  },
  mounted() {
    const q = this.$route.query || {};
    if (q.done) {
      this.done = true;
      this.isRenewal = q.renew === "1" || q.renew === 1;
      this.loading = false;
      return;
    }
    if (q.renew === "1" || q.renew === 1) {
      this.flowChoice = "renew";
    }
    this.initReferralFromUrl();
    this.init();
  },
  methods: {
    initReferralFromUrl() {
      const q = this.$route.query || {};
      let code = (q.ref || q.aff || "").toString().trim();
      if (code) {
        try {
          localStorage.setItem(this.referralStorageKey, code);
        } catch (e) {
          /* noop */
        }
      } else {
        try {
          code = (localStorage.getItem(this.referralStorageKey) || "").trim();
        } catch (e) {
          code = "";
        }
      }
      this.referralCode = code;
      if (code) this.validateReferralCode(code);
    },
    async validateReferralCode(code) {
      this.referralValid = false;
      this.referralPartnerName = "";
      this.referralEmailMasked = "";
      if (!code) return;
      try {
        const r = await axios.get("public/referral/validate", { params: { ref: code } });
        const data = (r.data && r.data.data) || {};
        if (r.data && r.data.success) {
          this.referralValid = true;
          this.referralPartnerName =
            (data.display_name || data.name || data.username || "").trim();
          this.referralEmailMasked = (data.email_masked || "").trim();
          this.referralCode = (data.code || code).trim();
        }
      } catch (e) {
        this.referralValid = false;
        this.referralPartnerName = "";
        this.referralEmailMasked = "";
      }
    },
    referralPayloadField() {
      return this.referralCode && this.referralValid ? this.referralCode : "";
    },
    normalizePackageId(id) {
      if (id === null || id === undefined || id === "") return null;
      const n = Number(id);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    packageIdMatch(a, b) {
      const left = this.normalizePackageId(a);
      const right = this.normalizePackageId(b);
      return left !== null && left === right;
    },
    findPackageById(id) {
      const pid = this.normalizePackageId(id);
      if (!pid) return null;
      return this.packages.find((p) => this.normalizePackageId(p.id) === pid) || null;
    },
    setFlowChoice(choice) {
      if (this.flowChoice === choice) return;
      this.flowChoice = choice;
      this.errorMsg = "";
      this.existsLogin = false;
      this.existingHint = false;
      this.accountNotFound = false;
      this.currentPackage = null;
      if (choice === "renew") {
        this.orderMode = "renew";
        this.isRenewal = true;
        this.form.name = "";
        this.form.password = "";
      } else {
        this.orderMode = "new";
        this.isRenewal = false;
      }
      const email = (this.form.email || "").trim();
      const phone = (this.form.phone || "").trim();
      if (email || phone) this.scheduleLookup();
    },
    scheduleLookup() {
      if (this.lookupTimer) clearTimeout(this.lookupTimer);
      this.lookupTimer = setTimeout(() => this.lookupAccount(), 500);
    },
    async lookupAccount() {
      const email = (this.form.email || "").trim();
      const phone = (this.form.phone || "").trim();
      this.existingHint = false;
      this.accountNotFound = false;
      if (!email && !phone) {
        this.orderMode = this.flowChoice === "renew" ? "renew" : "new";
        this.currentPackage = null;
        if (this.flowChoice === "new") this.isRenewal = false;
        return;
      }
      this.lookupLoading = true;
      try {
        const r = await axios.post("public/order/lookup", { email, phone });
        const data = (r.data && r.data.data) || {};
        if (data.is_renewal) {
          if (this.flowChoice === "renew") {
            this.orderMode = "renew";
            this.isRenewal = true;
            this.currentPackage = data.current_package || null;
            if (data.user) {
              this.form.name = data.user.name || this.form.name;
              this.form.email = data.user.email || this.form.email;
              this.form.phone = data.user.phone || this.form.phone;
            }
            if (data.packages && data.packages.length) {
              this.packages = data.packages;
              const selectedId = this.normalizePackageId(this.selectedPackageId);
              if (!selectedId || !this.findPackageById(selectedId)) {
                this.selectedPackageId = null;
              } else {
                this.selectedPackageId = selectedId;
              }
            }
          } else {
            this.orderMode = "new";
            this.isRenewal = false;
            this.currentPackage = data.current_package || null;
            this.existingHint = true;
          }
        } else if (data.exists) {
          this.orderMode = "existing";
          this.currentPackage = null;
          this.isRenewal = false;
          if (this.flowChoice === "new") this.existingHint = true;
        } else {
          if (this.flowChoice === "renew") {
            this.orderMode = "renew";
            this.isRenewal = true;
            this.currentPackage = null;
            this.accountNotFound = true;
          } else {
            this.orderMode = "new";
            this.currentPackage = null;
            this.isRenewal = false;
          }
        }
      } catch (e) {
        // keep current mode on lookup failure
      } finally {
        this.lookupLoading = false;
      }
    },
    async init() {
      this.loading = true;
      try {
        const [rp, rc, rm] = await Promise.all([
          axios.get("public/packages"),
          axios.get("public/payment-channels"),
          axios.get("public/payment-methods"),
        ]);
        this.packages = (rp.data && rp.data.data && rp.data.data.packages) || [];
        this.channels = (rc.data && rc.data.data && rc.data.data.payment_channel) || [];
        if (rm.data && rm.data.success) {
          this.paymentMethods = Object.assign(this.paymentMethods, rm.data.data || {});
        }
        if (this.paymentMethods.tripay_enabled) {
          this.payType = "tripay";
        } else if (this.paymentMethods.starqris_enabled) {
          this.payType = "starqris";
        } else if (this.paymentMethods.manual_transfer_enabled) {
          this.payType = "manual";
        }
        const pid = parseInt(this.$route.params.id);
        if (pid && this.findPackageById(pid)) {
          this.selectedPackageId = pid;
        }
        await this.prefillFromLoggedInUser();
        await this.applyOrderQueryParams();
      } catch (e) {
        this.errorMsg = "Gagal memuat data. Silakan muat ulang halaman.";
      } finally {
        this.loading = false;
      }
    },
    async applyOrderQueryParams() {
      const q = this.$route.query || {};
      const email = (q.email || "").toString().trim();
      const couponCode = (q.coupon || q.kupon || "").toString().trim();
      if (q.renew === "1" || q.renew === 1) {
        this.flowChoice = "renew";
        this.orderMode = "renew";
        this.isRenewal = true;
      }
      if (email) {
        this.form.email = email;
        await this.lookupAccount();
      }
      if (couponCode) {
        this.coupon.code = couponCode;
        if (this.selectedPackageId) {
          await this.checkCouponCode();
        }
      }
    },
    async prefillFromLoggedInUser() {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        let user = this.$store.state.auth && this.$store.state.auth.user;
        if (!user || !user.email) {
          const r = await this.$store.dispatch("user_detail");
          user = r && r.data && r.data.data && r.data.data.user;
        }
        if (!user || (!user.email && !user.phone)) return;

        this.flowChoice = "renew";
        this.orderMode = "renew";
        this.isRenewal = true;
        if (user.email) this.form.email = String(user.email).trim();
        if (user.phone) this.form.phone = String(user.phone).trim();
        if (user.name) this.form.name = String(user.name).trim();
        this.sessionPrefilled = true;
        await this.lookupAccount();
      } catch (e) {
        /* guest / expired token — biarkan isi manual */
      }
    },
    onProofChange(e) {
      this.proofFile = (e.target.files && e.target.files[0]) || null;
    },
    formatRp(v) {
      const n = Number(v) || 0;
      return "Rp" + n.toLocaleString("id-ID");
    },
    displayName(p) {
      const n = (p.name || "").trim();
      if (/basic lifetime/i.test(n)) return "Basic Lifetime";
      if (/premium lifetime/i.test(n)) return "Premium Lifetime";
      return n;
    },
    specs(p) {
      const parts = [];
      parts.push((p.max_device || 0) + " Device");
      parts.push(p.message_quota === -1 ? "Pesan Unlimited" : (p.message_quota || 0) + " Pesan");
      if (p.max_chat_gpt && p.max_chat_gpt > 0) parts.push("+ Chat AI");
      if ((p.duration || 0) >= 1000) parts.push("Lifetime");
      return parts.join(" · ");
    },
    resetCoupon() {
      this.coupon.discount = 0;
      this.coupon.status = "";
      this.coupon.message = "";
    },
    async checkCouponCode() {
      if (!this.coupon.code || !this.selectedPackageId) {
        this.coupon.status = "invalid";
        this.coupon.message = "Pilih paket dan isi kode kupon";
        return;
      }
      this.coupon.loading = true;
      this.coupon.status = "";
      this.coupon.message = "";
      this.coupon.discount = 0;
      try {
        const r = await axios.post("public/coupon/check", {
          code: this.coupon.code,
          package_id: this.selectedPackageId,
        });
        const data = (r.data && r.data.data) || {};
        this.coupon.discount = Number(data.discount) || 0;
        this.coupon.status = "valid";
        this.coupon.message = "Kupon valid — diskon " + this.formatRp(this.coupon.discount);
      } catch (e) {
        this.coupon.status = "invalid";
        this.coupon.message = (e.response && e.response.data && e.response.data.message) || "Kode kupon tidak valid";
        this.coupon.discount = 0;
      } finally {
        this.coupon.loading = false;
      }
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      this.errorMsg = "";
      this.existsLogin = false;
      const isRenew = this.flowChoice === "renew" && this.orderMode === "renew";
      try {
        if (this.payType === "manual") {
          const fd = new FormData();
          fd.append("email", this.form.email);
          fd.append("phone", this.form.phone);
          fd.append("package_id", String(this.selectedPackageId));
          fd.append("agreed", "1");
          fd.append("bank_account_id", String(this.manualForm.bank_account_id));
          fd.append("sender_name", this.manualForm.sender_name);
          fd.append("sender_bank", this.manualForm.sender_bank || "");
          fd.append("sender_account", this.manualForm.sender_account || "");
          fd.append("user_note", this.manualForm.user_note || "");
          if (this.coupon.status === "valid") fd.append("coupon_code", this.coupon.code);
          const refCode = this.referralPayloadField();
          if (refCode) fd.append("referral_code", refCode);
          const dt = new Date(this.manualForm.transfer_date_str);
          if (!isNaN(dt.getTime())) fd.append("transfer_date", String(dt.getTime()));
          fd.append("proof_image", this.proofFile);
          const manualUrl = isRenew ? "public/order/renew/manual" : "public/order/manual";
          if (!isRenew) {
            fd.append("name", this.form.name);
            fd.append("password", this.form.password);
          }
          const r = await axios.post(manualUrl, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (r.data && r.data.success) {
            this.doneManual = true;
            this.done = true;
            this.isRenewal = isRenew;
            return;
          }
          this.errorMsg = isRenew ? "Gagal mengirim konfirmasi perpanjangan." : "Gagal mengirim konfirmasi transfer.";
          return;
        }

        const orderUrl = isRenew ? "public/order/renew" : "public/order";
        const paymentChannel = this.payType === "starqris" ? "starqris" : this.selectedChannel;
        const refCode = this.referralPayloadField();
        const payload = isRenew
          ? {
              email: this.form.email,
              phone: this.form.phone,
              package_id: this.selectedPackageId,
              payment_channel: paymentChannel,
              coupon_code: this.coupon.status === "valid" ? this.coupon.code : "",
              referral_code: refCode,
              agreed: this.agreed,
            }
          : {
              name: this.form.name,
              email: this.form.email,
              phone: this.form.phone,
              password: this.form.password,
              package_id: this.selectedPackageId,
              payment_channel: paymentChannel,
              coupon_code: this.coupon.status === "valid" ? this.coupon.code : "",
              referral_code: refCode,
              agreed: this.agreed,
            };
        const r = await axios.post(orderUrl, payload, this.payType === "starqris" ? { timeout: 90000 } : {});
        const data = (r.data && r.data.data) || {};
        if (this.payType === "starqris" && data.starqris) {
          const trx = data.transaction || {};
          this.starqrisTransactionId = trx.transaction_id || trx.TransactionID || "";
          this.starqrisPayment = data.starqris;
          const email = encodeURIComponent((this.form.email || "").trim());
          this.starqrisStatusUrl = `public/order/status/${this.starqrisTransactionId}?email=${email}`;
          this.showStarQrisModal = true;
          return;
        }
        const url = data.checkout_url;
        if (url) {
          window.location.href = url;
          return;
        }
        this.errorMsg = "Gagal membuat pembayaran. Silakan coba lagi.";
      } catch (e) {
        const resp = e && e.response;
        this.errorMsg = (resp && resp.data && resp.data.message) || "Terjadi kesalahan. Silakan coba lagi.";
        if (resp && resp.status === 409) this.existsLogin = true;
      } finally {
        this.submitting = false;
      }
    },
    closeStarQrisModal() {
      this.showStarQrisModal = false;
    },
    onStarQrisPaid() {
      // polling sets paid state inside StarQrisPayment
    },
    onStarQrisDone() {
      this.showStarQrisModal = false;
      this.done = true;
      this.isRenewal = this.flowChoice === "renew";
    },
  },
};
</script>

<style scoped>
.po-wrap {
  max-width: 1040px;
  margin: 0 auto;
  padding: 18px 16px 60px;
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  color: #2b3042;
}
.po-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 2px 20px;
}
.po-referral-banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0 0 18px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #c9d8ff;
  background: linear-gradient(135deg, #eef3ff 0%, #f8fbff 100%);
  box-shadow: 0 2px 8px rgba(0, 60, 197, 0.08);
}
.po-referral-banner-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #003cc5;
  color: #fff;
  font-size: 16px;
}
.po-referral-banner-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #003cc5;
  margin-bottom: 4px;
}
.po-referral-banner-line {
  font-size: 15px;
  color: #2b3042;
  line-height: 1.45;
}
.po-referral-banner-code {
  margin-left: 4px;
  color: #5b647a;
  font-weight: 500;
}
.po-referral-banner-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7389;
}
.po-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #003cc5;
  font-size: 20px;
}
.po-brand i { color: #5f63f2; }
.po-login-link { font-size: 13px; color: #5b647a; text-decoration: none; }
.po-login-link b { color: #5f63f2; }
.po-title { text-align: center; margin-bottom: 18px; }
.po-title h2 { font-weight: 800; margin: 0 0 4px; }
.po-title p { color: #6b7280; margin: 0; font-size: 14px; }
.po-mode-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
.po-mode-tab {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 2px solid #eef0f6;
  background: #fff;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}
.po-mode-tab i {
  font-size: 20px;
  color: #9097a8;
  margin-top: 2px;
}
.po-mode-tab.active {
  border-color: #5f63f2;
  background: #f7f7fe;
  box-shadow: 0 4px 14px rgba(95, 99, 242, 0.12);
}
.po-mode-tab.active i { color: #5f63f2; }
.po-mode-tab-text { display: flex; flex-direction: column; gap: 2px; }
.po-mode-tab-text strong { font-size: 14px; color: #2b3042; }
.po-mode-tab-text span { font-size: 12px; color: #6b7280; line-height: 1.4; }
.po-mode-hint {
  background: #f8f9fb;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #5b647a;
  margin-bottom: 16px;
  line-height: 1.5;
}
.po-mode-hint i { color: #5f63f2; margin-right: 6px; }
.po-mode-hint a { color: #5f63f2; text-decoration: none; }
.po-mode-hint-renew {
  background: #eef5ff;
  border-color: #d8e6ff;
  color: #3a4154;
}
.po-warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 12.5px;
  border-radius: 9px;
  padding: 9px 11px;
}
.po-warn a { color: #92400e; }
.po-field-note {
  font-size: 13px;
  color: #6b7280;
  margin: -4px 0 12px;
  line-height: 1.5;
}
.po-loading { text-align: center; color: #6b7280; padding: 40px; }
.po-grid { display: grid; grid-template-columns: 1fr 360px; gap: 18px; align-items: start; }
.po-card {
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(20, 30, 80, 0.04);
}
.po-step {
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 14px;
}
.po-step span {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #5f63f2;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.po-group-label { font-size: 12px; font-weight: 700; color: #9097a8; text-transform: uppercase; margin-bottom: 8px; }
.mt-3 { margin-top: 16px; }
.po-pkg-list { display: flex; flex-direction: column; gap: 8px; }
.po-pkg {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #eef0f6;
  border-radius: 10px;
  padding: 11px 13px;
  cursor: pointer;
  transition: all 0.12s;
}
.po-pkg:hover { border-color: #c9cdf7; }
.po-pkg.active { border-color: #5f63f2; background: #f7f7fe; }
.po-pkg input { accent-color: #5f63f2; }
.po-pkg-body { flex: 1; }
.po-pkg-name { font-weight: 600; font-size: 14px; }
.po-pkg-specs { font-size: 12px; color: #8b91a3; margin-top: 2px; }
.po-pkg-price { font-weight: 800; color: #003cc5; font-size: 14px; }
.po-field { margin-bottom: 12px; }
.po-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 5px; }
.po-field input {
  width: 100%;
  border: 1.5px solid #e7e9f2;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}
.po-field input:focus { border-color: #5f63f2; }
.po-field small { color: #9097a8; font-size: 11.5px; }
.po-chgroup { margin-bottom: 12px; }
.po-ch-list { display: flex; flex-wrap: wrap; gap: 8px; }
.po-ch {
  border: 1.5px solid #eef0f6;
  border-radius: 9px;
  padding: 8px 10px;
  min-width: 78px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.12s;
}
.po-ch.active { border-color: #5f63f2; background: #f7f7fe; }
.po-ch input { display: none; }
.po-ch img { max-height: 24px; max-width: 64px; }
.po-ch-name { font-size: 11px; font-weight: 600; color: #5b647a; }
.po-pay-tabs { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.po-pay-tab {
  border: 1.5px solid #eef0f6;
  background: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #5b647a;
  cursor: pointer;
}
.po-pay-tab.active { border-color: #5f63f2; background: #f7f7fe; color: #003cc5; }
.po-pay-tab i { margin-right: 6px; }
.po-starqris-hint {
  background: #eef5ff;
  border: 1px solid #d8e6ff;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: #3a4154;
  margin-bottom: 12px;
  line-height: 1.5;
}
.po-starqris-hint i { color: #5f63f2; margin-right: 6px; }
.po-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 80, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.po-modal {
  background: #fff;
  border-radius: 14px;
  max-width: 420px;
  width: 100%;
  padding: 18px;
  box-shadow: 0 12px 40px rgba(20, 30, 80, 0.2);
}
.po-modal-head {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 12px;
  color: #003cc5;
}
.po-modal-head i { margin-right: 8px; color: #5f63f2; }
.po-manual { margin-top: 4px; }
.po-manual-info {
  background: #eef5ff;
  border: 1px solid #d8e6ff;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: #3a4154;
  margin-bottom: 12px;
}
.po-bank-list { display: flex; flex-direction: column; gap: 8px; }
.po-bank {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1.5px solid #eef0f6;
  border-radius: 10px;
  padding: 11px 13px;
  cursor: pointer;
}
.po-bank.active { border-color: #22a06b; background: #f3fbf7; }
.po-bank input { accent-color: #22a06b; margin-top: 3px; }
.po-bank code { font-size: 13px; }
.po-manual-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 640px) { .po-manual-row { grid-template-columns: 1fr; } }
.po-summary { position: sticky; top: 18px; }
.po-sum-row { display: flex; justify-content: space-between; font-size: 13.5px; padding: 6px 0; color: #5b647a; }
.po-sum-row b { color: #2b3042; }
.po-sum-total {
  display: flex;
  justify-content: space-between;
  font-size: 17px;
  font-weight: 800;
  padding: 12px 0;
  margin-top: 4px;
  border-top: 1px dashed #e7e9f2;
}
.po-sum-total b { color: #003cc5; }
.po-discount { color: #16a34a !important; }
.po-coupon { margin: 10px 0 12px; }
.po-coupon label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
.po-coupon-row { display: flex; gap: 8px; }
.po-coupon-row input {
  flex: 1;
  border: 1.5px solid #e7e9f2;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 14px;
}
.po-coupon-btn {
  border: 1.5px solid #5f63f2;
  background: #fff;
  color: #5f63f2;
  border-radius: 9px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
}
.po-coupon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.po-coupon-ok { display: block; margin-top: 6px; color: #16a34a; font-size: 12px; }
.po-coupon-bad { display: block; margin-top: 6px; color: #dc2626; font-size: 12px; }
.po-policy {
  background: #f8f9fb;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 12px;
  margin: 8px 0 14px;
}
.po-policy-title { font-size: 13px; font-weight: 700; margin-bottom: 6px; }
.po-policy-title i { color: #5f63f2; }
.po-policy ul { margin: 0 0 10px; padding-left: 18px; }
.po-policy li { font-size: 12px; color: #5b647a; line-height: 1.5; margin-bottom: 3px; }
.po-agree { display: flex; gap: 8px; align-items: flex-start; cursor: pointer; margin: 0; }
.po-agree input { margin-top: 2px; accent-color: #5f63f2; flex: 0 0 auto; }
.po-agree span { font-size: 12px; color: #3a4154; line-height: 1.5; }
.po-error {
  background: #fff2f2;
  border: 1px solid #ffd4d4;
  color: #c0392b;
  font-size: 12.5px;
  border-radius: 9px;
  padding: 9px 11px;
  margin-bottom: 12px;
}
.po-error a { color: #c0392b; }
.po-btn {
  display: block;
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
}
.po-btn-primary { background: linear-gradient(135deg, #5f63f2, #003cc5); color: #fff; }
.po-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.po-secure { text-align: center; font-size: 11.5px; color: #9097a8; margin-top: 9px; }
.po-secure i { color: #22a06b; }
.po-done { max-width: 520px; margin: 30px auto; text-align: center; }
.po-done-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e7f8f0;
  color: #22a06b;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}
.po-done h3 { font-weight: 800; }
.po-done p { color: #6b7280; font-size: 14px; line-height: 1.6; }
.po-renew-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #3a4154;
  line-height: 1.55;
}
.po-renew-banner b { color: #92400e; }
.po-expired { color: #dc2626; font-weight: 700; }
.po-active { color: #16a34a; font-weight: 700; }
.po-lookup { font-size: 12px; color: #6b7280; margin-bottom: 10px; }
@media (max-width: 860px) {
  .po-grid { grid-template-columns: 1fr; }
  .po-summary { position: static; }
  .po-mode-tabs { grid-template-columns: 1fr; }
}
</style>
